import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Edit, Plus, Trash2, X, Image, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SingleSubRoom = ({ cageNumber, roomLabel, animals, getAuthHeaders, onClose, onRefresh }) => {
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(false);
  const [animalDialogOpen, setAnimalDialogOpen] = useState(false);
  const [editingAnimal, setEditingAnimal] = useState(null);
  const [animalFormData, setAnimalFormData] = useState({
    animal_type: "rabbit",
    breed: "",
    age: "",
    gender: "male",
    purchase_date: "",
    birth_date: "",
    weight: "",
    health_status: "healthy",
    cage_number: cageNumber,
    sub_room: roomLabel,
    photo: "",
    group: "babies"
  });
  const [tempRoomData, setTempRoomData] = useState({
    capacity: 10,
    temperature: "",
    humidity: "",
    notes: ""
  });
  const animalPhotoRef = React.useRef(null);

  useEffect(() => {
    fetchRoomData();
  }, [cageNumber, roomLabel]);

  const fetchRoomData = async () => {
    try {
      const response = await axios.get(`${API}/sub-rooms?cage_number=${cageNumber}`, { headers: getAuthHeaders() });
      const room = response.data.find(r => r.room_label === roomLabel);
      
      if (!room) {
        // Create the room if it doesn't exist
        await axios.post(`${API}/sub-rooms`, {
          cage_number: cageNumber,
          room_label: roomLabel,
          capacity: 10
        }, { headers: getAuthHeaders() });
        fetchRoomData();
      } else {
        setRoomData(room);
        setTempRoomData({
          capacity: room.capacity || 10,
          temperature: room.temperature || "",
          humidity: room.humidity || "",
          notes: room.notes || ""
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching room data:", error);
      setLoading(false);
    }
  };

  const handleSaveRoomDetails = async () => {
    try {
      await axios.put(
        `${API}/sub-rooms/${cageNumber}/${roomLabel}`,
        tempRoomData,
        { headers: getAuthHeaders() }
      );
      toast.success("Room details updated");
      setEditingRoom(false);
      fetchRoomData();
    } catch (error) {
      toast.error("Failed to update room details");
    }
  };

  const getRoomAnimals = () => {
    return animals.filter(a => a.sub_room === roomLabel && a.cage_number === cageNumber);
  };

  const handleAddAnimal = () => {
    setEditingAnimal(null);
    setAnimalFormData({
      animal_type: "rabbit",
      breed: "",
      age: "",
      gender: "male",
      purchase_date: "",
      birth_date: "",
      weight: "",
      health_status: "healthy",
      cage_number: cageNumber,
      sub_room: roomLabel,
      photo: "",
      group: "babies"
    });
    setAnimalDialogOpen(true);
  };

  const handleEditAnimal = (animal) => {
    setEditingAnimal(animal);
    setAnimalFormData({
      animal_type: animal.animal_type,
      breed: animal.breed,
      age: animal.age,
      gender: animal.gender || "male",
      purchase_date: animal.purchase_date || "",
      birth_date: animal.birth_date || "",
      weight: animal.weight,
      health_status: animal.health_status,
      cage_number: animal.cage_number,
      sub_room: animal.sub_room || "",
      photo: animal.photo || "",
      group: animal.group
    });
    setAnimalDialogOpen(true);
  };

  const handleDeleteAnimal = async (animalId) => {
    if (window.confirm("Are you sure you want to delete this animal?")) {
      try {
        await axios.delete(`${API}/animals/${animalId}`, { headers: getAuthHeaders() });
        toast.success("Animal deleted successfully");
        onRefresh();
        fetchRoomData();
      } catch (error) {
        toast.error("Failed to delete animal");
      }
    }
  };

  const handleAnimalSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAnimal) {
        await axios.put(`${API}/animals/${editingAnimal.id}`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal updated successfully");
      } else {
        await axios.post(`${API}/animals`, animalFormData, { headers: getAuthHeaders() });
        toast.success("Animal added successfully");
      }
      setAnimalDialogOpen(false);
      onRefresh();
      fetchRoomData();
    } catch (error) {
      toast.error("Failed to save animal");
    }
  };

  const handleAnimalPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setAnimalFormData({...animalFormData, photo: reader.result});
    };
    reader.readAsDataURL(file);
  };

  const getHealthBadgeColor = (status) => {
    switch(status) {
      case "healthy": return "bg-green-500";
      case "sick": return "bg-red-500";
      case "under_treatment": return "bg-yellow-500";
      default: return "bg-gray-500";
    }
  };

  if (loading) {
    return <div className="p-4">Loading room data...</div>;
  }

  const roomAnimals = getRoomAnimals();

  return (
    <div className="single-sub-room-view p-6">
      <div className="flex items-center gap-4 mb-6">
        <Button variant="outline" size="sm" onClick={onClose}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cages
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Cage {cageNumber.replace('C-', '')} - Room {roomLabel}</h2>
          <p className="text-sm text-gray-600">{roomAnimals.length} / {roomData?.capacity || 10} animals</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Room Details Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Room Details</CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingRoom(!editingRoom)}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {editingRoom ? (
              <div className="space-y-3">
                <div>
                  <Label>Capacity</Label>
                  <Input
                    type="number"
                    value={tempRoomData.capacity}
                    onChange={(e) => setTempRoomData({...tempRoomData, capacity: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Temperature</Label>
                  <Input
                    value={tempRoomData.temperature}
                    onChange={(e) => setTempRoomData({...tempRoomData, temperature: e.target.value})}
                    placeholder="e.g., 22°C"
                  />
                </div>
                <div>
                  <Label>Humidity</Label>
                  <Input
                    value={tempRoomData.humidity}
                    onChange={(e) => setTempRoomData({...tempRoomData, humidity: e.target.value})}
                    placeholder="e.g., 60%"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={tempRoomData.notes}
                    onChange={(e) => setTempRoomData({...tempRoomData, notes: e.target.value})}
                    placeholder="Any additional notes..."
                  />
                </div>
                <Button onClick={handleSaveRoomDetails} className="w-full">Save Details</Button>
              </div>
            ) : (
              <div className="space-y-2">
                <div><strong>Capacity:</strong> {roomData?.capacity || 10} animals</div>
                {roomData?.temperature && <div><strong>Temperature:</strong> {roomData.temperature}</div>}
                {roomData?.humidity && <div><strong>Humidity:</strong> {roomData.humidity}</div>}
                {roomData?.notes && (
                  <div>
                    <strong>Notes:</strong>
                    <p className="text-sm text-gray-600 mt-1">{roomData.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Animals List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle>Animals in this Room</CardTitle>
              <Button onClick={handleAddAnimal}>
                <Plus className="w-4 h-4 mr-2" />
                Add Animal
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {roomAnimals.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No animals in this room yet</p>
                <Button onClick={handleAddAnimal} variant="outline" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Animal
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {roomAnimals.map(animal => (
                  <Card key={animal.id} className="border-2">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        {animal.photo && (
                          <img
                            src={animal.photo}
                            alt={animal.breed}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="font-bold text-lg">{animal.breed}</h3>
                              <p className="text-sm text-gray-600 capitalize">
                                {animal.animal_type.replace('_', ' ')} • {animal.gender} • {animal.age} months
                              </p>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditAnimal(animal)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteAnimal(animal.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                            <div><strong>Weight:</strong> {animal.weight} kg</div>
                            <div><strong>Group:</strong> {animal.group}</div>
                            {animal.birth_date && <div><strong>Birth Date:</strong> {animal.birth_date}</div>}
                            {animal.purchase_date && <div><strong>Purchase Date:</strong> {animal.purchase_date}</div>}
                          </div>
                          <div className="mt-2">
                            <Badge className={getHealthBadgeColor(animal.health_status)}>
                              {animal.health_status}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Animal Form Dialog */}
      {animalDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>{editingAnimal ? "Edit Animal" : "Add New Animal"}</CardTitle>
              <CardDescription>
                {editingAnimal ? "Update animal details" : `Add animal to Cage ${cageNumber.replace('C-', '')} - Room ${roomLabel}`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleAnimalSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Animal Type</Label>
                    <Select 
                      value={animalFormData.animal_type} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, animal_type: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rabbit">Rabbit</SelectItem>
                        <SelectItem value="grass_cutter">Grass Cutter</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Breed</Label>
                    <Input
                      value={animalFormData.breed}
                      onChange={(e) => setAnimalFormData({...animalFormData, breed: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Age (months)</Label>
                    <Input
                      type="number"
                      value={animalFormData.age}
                      onChange={(e) => setAnimalFormData({...animalFormData, age: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select 
                      value={animalFormData.gender} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, gender: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Weight (kg)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={animalFormData.weight}
                      onChange={(e) => setAnimalFormData({...animalFormData, weight: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Health Status</Label>
                    <Select 
                      value={animalFormData.health_status} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, health_status: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="healthy">Healthy</SelectItem>
                        <SelectItem value="sick">Sick</SelectItem>
                        <SelectItem value="under_treatment">Under Treatment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={animalFormData.purchase_date}
                      onChange={(e) => setAnimalFormData({...animalFormData, purchase_date: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Birth Date</Label>
                    <Input
                      type="date"
                      value={animalFormData.birth_date}
                      onChange={(e) => setAnimalFormData({...animalFormData, birth_date: e.target.value})}
                    />
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Group</Label>
                    <Select 
                      value={animalFormData.group} 
                      onValueChange={(value) => setAnimalFormData({...animalFormData, group: value})}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="babies">Babies</SelectItem>
                        <SelectItem value="mothers">Mothers</SelectItem>
                        <SelectItem value="crossed">Crossed</SelectItem>
                        <SelectItem value="pregnant">Pregnant</SelectItem>
                        <SelectItem value="ready_to_sell">Ready to Sell</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2 col-span-2">
                    <Label>Animal Photo</Label>
                    <input
                      type="file"
                      ref={animalPhotoRef}
                      accept="image/*"
                      className="hidden"
                      onChange={handleAnimalPhotoSelect}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => animalPhotoRef.current?.click()}
                      className="w-full"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      {animalFormData.photo ? "Change Photo" : "Upload Photo"}
                    </Button>
                    {animalFormData.photo && (
                      <div className="relative inline-block mt-2">
                        <img
                          src={animalFormData.photo}
                          alt="Animal preview"
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          className="absolute top-1 right-1"
                          onClick={() => setAnimalFormData({...animalFormData, photo: ""})}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setAnimalDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingAnimal ? "Update" : "Add"} Animal
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SingleSubRoom;
